import { CLASS_OF_SERVICE } from '../constants';

// Generate FlightAware URL for a flight
export const generateFlightAwareUrl = (flightData) => {
  // Extract flight number (e.g., "UA 001" -> "UAL1")
  const flightNum = flightData.flightNumber
    .replace(/\s+/g, '')
    .replace(/^UA/, 'UAL')
    .replace(/^0+/, ''); // Remove leading zeros
  
  // Format date for FlightAware (YYYYMMDD)
  const dateFormatted = flightData.date.replace(/-/g, '');
  
  // Departure airport ICAO (add K prefix for US airports if 3 letters)
  const depIcao = flightData.departure.length === 3 
    ? `K${flightData.departure}` 
    : flightData.departure;
  
  const arrIcao = flightData.arrival.length === 3 
    ? `K${flightData.arrival}` 
    : flightData.arrival;

  // FlightAware URL format
  return `https://flightaware.com/live/flight/${flightNum}/history/${dateFormatted}/${depIcao}/${arrIcao}`;
};

// Get rarity info for a class of service
export const getRarityInfo = (classOfService) => {
  const classInfo = CLASS_OF_SERVICE.find(c => c.value === classOfService);
  return classInfo || { rarity: "common", color: "#6B7280" };
};

// Generate OpenSea-compatible metadata
export const generateMetadata = (flightData, imageURI) => {
  const rarityInfo = getRarityInfo(flightData.classOfService);
  const flightAwareUrl = generateFlightAwareUrl(flightData);
  
  const metadata = {
    name: `United ${flightData.flightNumber}: ${flightData.departure} → ${flightData.arrival}`,
    description: `Flight segment NFT commemorating United Airlines flight ${flightData.flightNumber} from ${flightData.departure} to ${flightData.arrival} on ${flightData.date}. This digital collectible captures your journey across the skies.`,
    image: imageURI,
    external_url: flightAwareUrl,
    attributes: [
      { trait_type: "Flight Number", value: flightData.flightNumber },
      { trait_type: "Origin", value: flightData.departure },
      { trait_type: "Destination", value: flightData.arrival },
      { trait_type: "Date", value: flightData.date },
      { trait_type: "Aircraft", value: flightData.aircraft },
      { trait_type: "Tail Number", value: flightData.tailNumber },
      { trait_type: "Seat", value: flightData.seat },
      { trait_type: "Class of Service", value: flightData.classOfService },
      { trait_type: "Rarity", value: rarityInfo.rarity },
      { trait_type: "Seat Configuration", value: flightData.seatConfiguration },
      { trait_type: "Duration", value: flightData.duration },
    ],
  };

  // Add optional crew attributes
  if (flightData.captain) {
    metadata.attributes.push({ trait_type: "Captain", value: flightData.captain });
  }
  if (flightData.firstOfficer) {
    metadata.attributes.push({ trait_type: "First Officer", value: flightData.firstOfficer });
  }
  if (flightData.cabinCrew) {
    metadata.attributes.push({ trait_type: "Cabin Crew", value: flightData.cabinCrew });
  }

  // Add optional gate attributes
  if (flightData.departureGate) {
    metadata.attributes.push({ trait_type: "Departure Gate", value: flightData.departureGate });
  }
  if (flightData.arrivalGate) {
    metadata.attributes.push({ trait_type: "Arrival Gate", value: flightData.arrivalGate });
  }

  // Add lounge add-ons
  const lounges = [];
  if (flightData.lounges?.departurePolarisLounge) {
    lounges.push(`Polaris Lounge @ ${flightData.departure}`);
  }
  if (flightData.lounges?.departureUnitedClub) {
    lounges.push(`United Club @ ${flightData.departure}`);
  }
  if (flightData.lounges?.arrivalPolarisLounge) {
    lounges.push(`Polaris Lounge @ ${flightData.arrival}`);
  }
  if (flightData.lounges?.arrivalUnitedClub) {
    lounges.push(`United Club @ ${flightData.arrival}`);
  }
  
  if (lounges.length > 0) {
    metadata.attributes.push({ trait_type: "Lounge Visits", value: lounges.join(", ") });
    // Also add individual boolean traits for filtering
    if (flightData.lounges?.departurePolarisLounge || flightData.lounges?.arrivalPolarisLounge) {
      metadata.attributes.push({ trait_type: "Polaris Lounge", value: "Yes" });
    }
    if (flightData.lounges?.departureUnitedClub || flightData.lounges?.arrivalUnitedClub) {
      metadata.attributes.push({ trait_type: "United Club", value: "Yes" });
    }
  }

  return metadata;
};

// Format time for display
export const formatTime = (time) => {
  if (!time) return '';
  // Handle both "08:00" and "8:00 AM" formats
  if (time.includes(':')) {
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  }
  return time;
};

// Parse duration to minutes for comparison
export const parseDuration = (duration) => {
  if (!duration) return 0;
  const match = duration.match(/(\d+)h\s*(\d+)?m?/);
  if (match) {
    const hours = parseInt(match[1]) || 0;
    const minutes = parseInt(match[2]) || 0;
    return hours * 60 + minutes;
  }
  return 0;
};
